(ns app.front.view.group.reporting.code-report
  (:require [ajax.core :refer [POST]]
            [app.domain.group.config.model :as config-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.file.upload :as upload]
            [app.front.ui :as ui]
            [app.front.ui.add-widget :as ui-add]
            [app.front.ui.config-pills :as ui-config-pills]
            [app.front.ui.section :as section]
            [app.front.ui.separator :as ui-sep]
            [app.front.ui.tags :as ui-tags]
            [app.front.util :as futil :refer [for-frag for-frag-indexed]]
            [app.front.view.group.admin.permit-type-select :as permit-type-select]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.reporting.common :as reporting-common]
            [app.front.view.group.reporting.download-button :as rdb]
            [app.front.view.group.reporting.list :as report-list]
            [app.reporting.code-report-model :as code-report-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.entity.tree :as entity-tree]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [re-frame.std-interceptors :as interceptors]
            [schema.core :as s]))

(rf/reg-event-fx ::fetch-mappings
  (fn [{:keys [db]} [_ group-id]]
    {:db           (assoc-in db [::state :loading?] true)
     :comm/command {:command    :report.code/get-mappings
                    :method     :get
                    :data       (util/mmap group-id)
                    :on-success [::fetch-mappings.success]}}))

(rf/reg-event-fx ::fetch-mappings.success
  (fn [{:keys [db]} [_ status entities]]
    {:db (assoc-in db [::state :loading?] false)
     :fx [[:dispatch [::entities/merge-entity-db status entities]]]}))

(defn dissoc-work-target [tuple]
  (take 3 tuple))

(rf/reg-event-fx ::set-mapping
  (interceptors/path ::state)
  (fn [{:keys [db]} [_ mapping-set-id mapping-group-id config-ids]]
    {:comm/command {:command    :report.code/set-mapping
                    :method     :post
                    :data       (util/mmap config-ids mapping-set-id mapping-group-id)
                    :on-success [::entities/apply-changes]}
     :db           (-> db
                       (assoc :show-editor? false)
                       (update :path dissoc-work-target))}))

(rf/reg-event-fx ::remove-mapping
  (fn [_ [_ mapping-set-id config-ids]]
    {:comm/command {:command    :report.code/remove-mapping
                    :method     :post
                    :data       (util/mmap config-ids mapping-set-id)
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::remove-group
  (fn [_ [_ mapping-set-id mapping-group-id]]
    {:comm/command {:command    :report.code/remove-group
                    :method     :post
                    :data       (util/mmap mapping-group-id mapping-set-id)
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::add-stop-work-order
  (fn [_ [_ mapping-set-id order-template-id]]
    {:comm/command {:command    :report.code/add-stop-work-order
                    :method     :post
                    :data       (util/mmap order-template-id mapping-set-id)
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::remove-stop-work-order
  (fn [_ [_ mapping-set-id order-template-id]]
    {:comm/command {:command    :report.code/remove-stop-work-order
                    :method     :post
                    :data       (util/mmap order-template-id mapping-set-id)
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::save-group-edits
  (interceptors/path ::state)
  (fn [{{:keys [editing-group-id edited-group]} :db} [_ mapping-set-id]]
    {:comm/command {:command    :report.code/edit-mapping
                    :method     :post
                    :data       (merge (util/mmap {:mapping-group-id editing-group-id}
                                                  mapping-set-id)
                                       edited-group)
                    :on-success [::save-group-edits.success]
                    :notify     {:success :report.code/group-saved}}}))

(rf/reg-event-fx ::save-group-edits.success
  (interceptors/path ::state)
  (fn [{:keys [db]} [_ status resp]]
    {:dispatch [::entities/apply-changes status resp]
     :db       (-> db
                   (dissoc :editing-group-id :edited-group))}))

;;; Import / Export

(rf/reg-sub ::import-warnings
  :<- [::state]
  :-> :import-warnings)

(rf/reg-event-db ::clear-import-warnings
  (interceptors/path ::state)
  (fn [state _]
    (dissoc state :import-warnings)))

(defn- stop-tracking-file [component-id file-hash]
  (>evt [::upload/remove-pending-file component-id :placeholder])
  (>evt [::upload/remove-pending-file component-id file-hash]))

(defn- process-import-file
  [mapping-set-id {:keys [file component-id file-hash]}]
  (>evt [::upload/add-pending-file component-id file-hash])
  (POST (str "/api/command/report/code/import-mappings?mapping-set-id=" mapping-set-id)
    {:headers       {"Content-Type" "application/edn"}
     :body          file
     :error-handler (fn [{:keys [status response]}]
                      (stop-tracking-file component-id file-hash)
                      (>evt [::upload/upload-failure (util/mmap {:md5 file-hash} component-id) status response]))
     :handler       (fn [response]
                      (>evt [::entities/apply-changes 200 (:tx-changes response)])
                      (stop-tracking-file component-id file-hash)
                      (>evt [:notifications/add :toast :success :success :report.code/import-success])
                      (when (seq (:warnings response))
                        (>evt [::set-import-warnings (:warnings response)])))}))

(rf/reg-event-db ::set-import-warnings
  (interceptors/path ::state)
  (fn [state [_ warnings]]
    (assoc state :import-warnings warnings)))

(defn get-parent-config-names [lang config]
  ;; don't return permit-type config name, so omit current name if we are at the top level
  (when-let [parent (:entity/parent config)]
    (concat (get-parent-config-names lang parent)
            [(config-model/localized-name config lang)])))

(rf/reg-sub ::sorted-config-names
  (fn [[_ config-ids]]
    [(rf/subscribe [::entities/entities config-ids])
     (rf/subscribe [:language])])
  (fn [[config-entities lang] _]
    (->> config-entities
         (map (fn [config]
                (let [names (get-parent-config-names lang config)]
                  {:title (->> names reverse rest reverse (str/join " / "))
                   :name  (last names)
                   :id    (:db/id config)})))
         (sort-by :name)
         (group-by :title)
         (sort-by first))))

(rf/reg-sub ::child-config-ids
  :<- [::selected-config-id]
  :<- [::entities/context]
  (fn [[config-id ctx] _]
    (when-not (set? config-id)
      (-> (entities/find-entity ctx config-id)
          eu/descendants
          du/entity-id-set))))

(rf/reg-sub ::with-child-indications
  (fn [[_ codes]]
    (rf/subscribe [::entities/entities (-> codes keys set)]))
  (fn [configs [_ codes]]
    ;; add '*' to indicate configs that have settings on a lower level
    (->> configs
         (mapcat (comp entity-tree/eager-ancestors :entity/parent))
         (map :db/id)
         (reduce (fn [codes parent-config-id]
                   (assoc codes parent-config-id "*"))
                 codes))))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::loading-mappings?
  :<- [::state]
  :-> :loading?)

(rf/reg-sub ::loading?
  :<- [::loading-mappings?]
  :<- [::reporting-common/loading-configs?]
  (fn [loading-flags _]
    (some true? loading-flags)))

(rf/reg-event-db ::select-path
  (interceptors/path ::state)
  (fn [db [_ new-path]]
    (-> db
        (assoc :show-editor? false)
        (assoc :path new-path))))

(rf/reg-sub ::show-editor?
  :<- [::state]
  (fn [state]
    (get state :show-editor? true)))

(rf/reg-event-db ::open-editor
  (interceptors/path ::state)
  (fn [state [_ mapping-group-id code description]]
    (-> state
        (assoc :editing-group-id mapping-group-id)
        (assoc :show-editor? false)
        (assoc :edited-group {:code-report-mapping/code        code
                              :code-report-mapping/description description}))))

(rf/reg-sub ::editing-group-id
  :<- [::state]
  :-> :editing-group-id)

(rf/reg-sub ::editing-group?
  :<- [::editing-group-id]
  (fn [editing-id [_ polled-id]]
    (= editing-id polled-id)))

(rf/reg-event-db ::set-show-editor
  (interceptors/path ::state)
  (fn [db [_ shown?]]
    (-> db
        (assoc :show-editor? shown?)
        (assoc :editing-group-id nil)
        (assoc :edited-group {:code-report-mapping/code        ""
                              :code-report-mapping/description ""}))))

(rf/reg-sub ::group-editor-data
  :<- [::state]
  :<- [::existing-group-codes]
  :<- [::editing-group-id]
  (fn [[{{:code-report-mapping/keys [code] :as data} :edited-group} group-codes mapping-group-id] _]

    (-> data
        (assoc :valid? (and (or (not (contains? group-codes code))
                                (= (get group-codes code) mapping-group-id))
                            (not (str/blank? code)))))))

(rf/reg-sub ::loading-edit-group?
  :<- [::state]
  :-> :loading-edit-group?)

(defn- handle-set-fn [attr]
  (fn [state [_ value]]
    (assoc-in state [:edited-group attr] value)))

(rf/reg-event-db ::set-group-code
  (interceptors/path ::state)
  (handle-set-fn :code-report-mapping/code))

(rf/reg-event-db ::set-group-desc
  (interceptors/path ::state)
  (handle-set-fn :code-report-mapping/description))

(rf/reg-event-fx ::create-group
  (interceptors/path ::state)
  (fn [{:keys [db]} [_ mapping-set-id config-ids]]
    {:db           (-> db
                       (assoc :loading-edit-group? true)
                       (update :path dissoc-work-target))
     :comm/command {:command    :report.code/create-group
                    :on-success [::create-group.success]
                    :data       (merge (:edited-group db)
                                       (util/mmap mapping-set-id config-ids))
                    :notify     {:success :report.code/group-created}}}))

(rf/reg-event-fx ::create-group.success
  (interceptors/path ::state)
  (fn [{:keys [db]} [_ status resp]]
    {:db (assoc db :loading-edit-group? false)
     :fx [[:dispatch [::entities/apply-changes status resp]]
          [:dispatch [::cancel-edit-group]]]}))

(rf/reg-event-db ::cancel-edit-group
  (interceptors/path ::state)
  (fn [state _]
    (-> state
        (dissoc :edited-group)
        (assoc :editing-group-id nil)
        (assoc :show-editor? false))))

(rf/reg-sub ::selected-path
  :<- [::state]
  :<- [::gas/group-primary-permit-type]
  (fn [[state primary-permit-type] _]
    (get state :path
         ;; by default, choose the primary permit type
         [primary-permit-type])))

(rf/reg-sub ::path-name
  :<- [:language]
  :<- [::entities/context]
  :<- [::selected-path]
  (fn [[lang ctx tuple] _]
    (->> tuple
         rest
         (map #(if (set? %)
                 (set (entities/find-entities ctx %))
                 (entities/find-entity ctx %)))
         (config-model/config-path-as-string lang))))

(rf/reg-sub ::selected-config-id
  :<- [::selected-path]
  :-> last)

;; Technically, there can be multiple mapping sets (aka report parameter sets), but for now, we behave
;; as if there is only one. To extend this, you will need UI for creating and selecting them.
(rf/reg-sub ::mapping-set
  :<- [::gas/group]
  (fn [group _]
    (-> group code-report-model/group-mapping-sets first)))

(rf/reg-sub ::codes
  :<- [::mapping-set]
  (fn [mapping-set _]
    (->> mapping-set
         :code-report-mapping/_mapping-set
         (mapcat (fn [{:code-report-mapping/keys [code group-configs]}]
                   (->> group-configs
                        (map (fn [gc]
                               [(:db/id gc) code])))))
         (into {}))))

(rf/reg-sub ::selected-group-for-configs
  :<- [::mapping-set]
  (fn [mapping-set [_ config-ids]]
    (let [mappings (->> mapping-set
                        :code-report-mapping/_mapping-set
                        (filter (fn [{:code-report-mapping/keys [group-configs]}]
                                  (seq (set/intersection (du/entity-id-set group-configs)
                                                         config-ids))))
                        distinct)]
      (util/only mappings))))

(rf/reg-sub ::sorted-mapping-groups
  :<- [::mapping-set]
  (fn [mapping-set _]
    (->> mapping-set
         :code-report-mapping/_mapping-set
         (sort-by :code-report-mapping/code))))

(rf/reg-sub ::mapping-groups-pill-items
  :<- [::sorted-mapping-groups]
  (fn [mapping-groups _]
    (->> mapping-groups
         (map (fn [{id :db/id code :code-report-mapping/code}]
                {:value id :text code})))))

(rf/reg-sub ::existing-group-codes
  :<- [::mapping-groups-pill-items]
  (fn [groups _]
    (->> groups
         (map (juxt :text :value))
         (into {}))))

(rf/reg-sub ::mapping-set-id
  :<- [::mapping-set]
  :-> :db/id)

(def report-type :reporting/code-report)

(rf/reg-sub ::query
  :<- [::gas/group-id]
  :<- [::mapping-set-id]
  :<- [::reporting-common/date-range]
  (fn [[group-id mapping-set-id [start-date end-date]] _]
    (util/mmap report-type group-id mapping-set-id start-date end-date)))

(defn mapped-configs [{:keys [tr config-ids can-edit?]}]
  (let [sorted-configs (<sub [::sorted-config-names config-ids])
        mapping-set-id (<sub [::mapping-set-id])]
    [:div
     (for-frag-indexed [[ix [title configs]] sorted-configs]
       [:div
        (when-not (= ix 0)
          [ui-sep/horizontal])
        [:div.mb-4.font-bold title]
        (for-frag [{:keys [id name]} configs]
          [ui-tags/tag {:label-text name
                        :buttons    [(when can-edit?
                                       {:description (tr [:report.code/remove-mapping])
                                        :icon        i/remove
                                        :on-click    #(>evt [::remove-mapping mapping-set-id #{id}])})]}])])]))

(defn group-editor [{:keys [tr title help on-apply apply-text]}]
  (let [{:code-report-mapping/keys [code description]
         :keys                     [valid?]}          (<sub [::group-editor-data])]
    [ui-add/add-widget
     (util/mmap {:loading? (<sub [::loading-edit-group?])
                 :buttons  [{:on-click #(>evt [::cancel-edit-group])
                             :text     (tr [:cancel])}
                            {:on-click  on-apply
                             :disabled? (not valid?)
                             :text      apply-text}]}
                title help)
     [:div.space-y-2
      [:div.mt-2.flex.gap-2.flex-wrap
       [:div.flex-1
        [ui/text-input {:value      code
                        :light      true
                        ;; highlight duplicate code, but not empty code (that should be obvious)
                        :invalid    (and (not (str/blank? code)) (not valid?))
                        :label-text (tr [:report.code/group-code])
                        :on-change  #(>evt [::set-group-code (futil/tval %)])}]]
       [:div.flex-auto
        [ui/text-input {:value      description
                        :light      true
                        :label-text (tr [:report.code/group-description])
                        :on-change  #(>evt [::set-group-desc (futil/tval %)])}]]]]]))

(defn visualize-single-mapping [{:keys [tr mapping-group can-edit?]}]
  (let [config-ids       (-> mapping-group :code-report-mapping/group-configs du/entity-id-set)
        mapping-set-id   (<sub [::mapping-set-id])
        mapping-group-id (:db/id mapping-group)
        editing          (<sub [::editing-group? mapping-group-id])]
    [section/subsection-2
     (util/mmap {:title          (code-report-model/mapping-text mapping-group)
                 :header-buttons [(when can-edit?
                                    {:kind        :tertiary
                                     :on-click    #(>evt [::remove-group mapping-set-id mapping-group-id])
                                     :icon        i/remove
                                     :description (tr [:report.code/remove-group])})
                                  (when (and can-edit? (not editing))
                                    {:kind        :primary
                                     :on-click    #(>evt [::open-editor
                                                          mapping-group-id
                                                          (:code-report-mapping/code mapping-group)
                                                          (:code-report-mapping/description mapping-group)])
                                     :icon        i/pen
                                     :description (tr [:report.code/edit-mapping])})]}
                tr)
     (when editing
       [group-editor (util/mmap {:title      (tr [:report.code/edit-mapping-long])
                                 :on-apply   #(>evt [::save-group-edits mapping-set-id])
                                 :apply-text (tr [:save])}
                                tr)])
     [mapped-configs (util/mmap tr config-ids can-edit?)]]))

(defn visualize-mappings [{:keys [tr can-edit?]}]
  (for-frag [mapping-group (<sub [::sorted-mapping-groups])]
    [visualize-single-mapping (util/mmap tr mapping-group can-edit?)]))

(defn ->set [v]
  (if (set? v) v #{v}))

(defn mapping-editor [{:keys [tr codes]}]
  [:div.mt-6
   [:div.mb-4
    [:span.font-bold.mr-1 (tr [:report.code/selected-application-type-title])]
    (<sub [::path-name])]
   (let [selected-path      (<sub [::selected-path])
         inherited-value    (->> selected-path
                                 ;; drop permit-type
                                 rest
                                 ;; drop current config-id
                                 drop-last
                                 ;; chech if any parent has a value set
                                 (some (fn [config-id] (get codes config-id))))
         selected-config-id (<sub [::selected-config-id])
         selected-group     (<sub [::selected-group-for-configs (->set selected-config-id)])
         value              (:db/id selected-group)
         disabled           (some? inherited-value)
         child-ids          (<sub [::child-config-ids])
         show-editor?       (<sub [::show-editor?])
         mapping-set-id     (<sub [::mapping-set-id])]
     (if (some codes child-ids)
       [:div
        [ui/help (tr [:report.code/mappings-on-lower-level]) {:color :blue}]
        (cc/button {:kind     :ghost
                    :icon     i/remove
                    :on-click #(>evt [::remove-mapping mapping-set-id child-ids])}
                   (tr [:report.code/clear-lower-level]))]
       [:div
        (if inherited-value
          [ui/help (tr [:report.code/value-selected-higher-level] [inherited-value]) {:color :blue}]
          [:<>
           [:span.mb-2 (tr [:report.code/select-value])]
           (->> (<sub [::mapping-groups-pill-items])
                (into [ui/select-pills {:on-change #(>evt [::set-mapping mapping-set-id % (->set selected-config-id)])
                                        :disabled? disabled
                                        :value     value}]))
           (when selected-group
             [:div.my-4.ml-4.font-semibold.text-ui-night-sky-80 (code-report-model/mapping-text selected-group)])
           [:div.flex.mt-4
            (if value
              (cc/button {:kind     :ghost
                          :icon     i/remove
                          :on-click #(>evt [::remove-mapping mapping-set-id (->set selected-config-id)])}
                         (tr [:report.code/clear-mapping]))
              (when-not show-editor?
                (cc/button {:kind     :ghost
                            :icon     i/plus
                            :on-click #(>evt [::set-show-editor true])}
                           (tr [:report.code/add-new]))))]])
        (when show-editor?
          [group-editor (util/mmap {:title      (tr [:report.code/create-mapping])
                                    :help       (tr [:report.code/create-mapping-help])
                                    :on-apply   #(>evt [::create-group mapping-set-id (->set selected-config-id)])
                                    :apply-text (tr [:report.code/create-mapping-short])}
                                   selected-config-id tr)])]))])

(defn import-warnings-panel [{:keys [tr]}]
  (if-let [warnings (seq (<sub [::import-warnings]))]
    [:div.mt-4
     [ui/help
      [:div
       [:div.font-bold.mb-2 (tr [:report.code/import-warnings-title])]
       [:ul.list-disc.ml-4
        (for-frag [w warnings]
          [:li w])]
       [:div.mt-2
        (cc/button {:kind     :ghost
                    :size     :small
                    :on-click #(>evt [::clear-import-warnings])}
                   (tr [:close]))]]
      {:color :yellow}]]
    [:div]))

(defn mapping-section [{:keys [tr]}]
  (let [codes          (<sub [::codes])
        selected-tuple (<sub [::selected-path])
        can-edit?      (<sub [::gas/is-admin?])
        mapping-set-id (<sub [::mapping-set-id])]
    [:section
     [:h3 (tr [:report.code/mapping-title])]
     (when (and (<sub [:session/admin?]) mapping-set-id)
       ;; for superadmins only
       [:div.flex.gap-2.mb-4
        (cc/button {:kind :tertiary
                    :size :small
                    :icon i/download
                    :href (str "/api/command/report/code/export-mappings?mapping-set-id=" mapping-set-id)}
                   (tr [:report.code/export-mappings]))
        [upload/file-picker-button
         {:id           (str "code-report-import-" mapping-set-id)
          :tr           tr
          :accept-types [".edn"]
          :button-opts  {:kind             :tertiary
                         :size             :small
                         :icon             i/upload
                         :icon-description (tr [:report.code/import-mappings])}
          :on-select-fn (partial process-import-file mapping-set-id)}
         (tr [:report.code/import-mappings])]])
     [import-warnings-panel (util/mmap tr)]
     [:<>
      (when can-edit?
        [:<>
         [ui/help
          (tr [:report.code/mapping-help])
          {:color :blue}]
         [permit-type-select/permit-type-select
          (util/mmap {:config-id->value     (<sub [::with-child-indications codes])
                      :on-change            #(>evt [::select-path %])
                      :multi-select-target? true}
                     tr selected-tuple)]
         (if (-> selected-tuple rest seq)
           [mapping-editor (util/mmap tr codes)]
           [ui/help (tr [:report.code/select-type-to-start]) {:color :blue}])])
      (when (seq codes)
        [:div.mt-8
         [:h4.mb-4 (tr [:report.code/all-mappings-title])]
         [visualize-mappings (util/mmap tr can-edit?)]])]]))

(rf/reg-sub ::order-templates
  :<- [::gas/group]
  :-> inspection-model/order-templates-from-group)

(rf/reg-sub ::selected-order-templates
  :<- [::mapping-set]
  (fn [mapping-set _]
    (->> mapping-set
         :code-report-mapping-set/stop-work-orders
         (remove nil?))))

(defn orders-section [{:keys [tr]}]
  (when (<sub [::gas/has-feature? :group.feature/orders-for-inspections-enabled])
    (let [lang           (<sub [:language])
          mapping-set-id (<sub [::mapping-set-id])
          can-edit?      (<sub [::gas/is-admin?])]
      [:section
       [:h3 (tr [:report.code/stop-work-order-types])]
       [ui-config-pills/config-pills
        (util/mmap {:values      (<sub [::order-templates])
                    :selected    (<sub [::selected-order-templates])
                    :val->str    #(inspection-model/order-template-name % lang)
                    :remove-item (when can-edit?
                                   #(>evt [::remove-stop-work-order mapping-set-id (:db/id %)]))
                    :add-item    (when can-edit?
                                   #(>evt [::add-stop-work-order mapping-set-id (:db/id %)]))}
                   tr)]])))

(defn report-section [{:keys [tr]}]
  (let [query (<sub [::query])]
    [:section
     [report-list/list-section (util/mmap tr report-type)]
     [:div
      [reporting-common/date-range-selector tr "code-report-date-range" reporting-common/date-range-options]]
     [rdb/generate-button (util/mmap tr query)]]))

(defn view [match]
  (let [tr (<sub [::gas/group-tr])]
    [reporting-common/report-page
     match
     tr
     (if (<sub [::loading?])
       [:<>
        [section/section-skeleton]
        [section/section-skeleton]
        [section/section-skeleton]]
       [:<>
        [mapping-section (util/mmap tr)]
        [orders-section (util/mmap tr)]
        [report-section (util/mmap tr)]])]))

(def route
  {:name        :view/group.reporting.code-report
   :view        view
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [{{:keys [group-id]} :path}]
                                (>evt [::fetch-mappings group-id])
                                (>evt [::reporting-common/fetch-reports group-id])
                                (>evt [::reporting-common/fetch-group group-id])
                                (>evt [::reporting-common/fetch-group-configs group-id]))}]})
